import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import DangerousIcon from '@mui/icons-material/Dangerous';
import PlusOneIcon from '@mui/icons-material/PlusOne';

import { Badge, Box, Chip, IconButton } from '@mui/material';
import {
  useRecordContext,
  Labeled,
  useGetIdentity,
} from 'react-admin';
import { useOnSettled } from '../therapistAppConfig/useOnSettled';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { adminRpcClient } from '@/providers/backendClient';

export const PaidSubscriptionInfo = () => {
  const record = useRecordContext();

  const userId = useGetIdentity().data?.id?.toString();

  const { onSettled } = useOnSettled();

  const { isPending, mutateAsync: onSyncSubscription } = useMutation({
    mutationKey: ['PUBLIC_ADMIN_syncRevenueCat', record.id],
    mutationFn: async () => {
      console.warn('RECORD ID', record.id);

      await adminRpcClient('PUBLIC_ADMIN_syncRevenueCat', [
        record.id,
      ]);
    },
    onSettled,
  });

  const [historyExpanded, setExpanded] = useState(false);

  const onToggleHistoryExpansion = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  return (
    <>
      <Box marginTop="2rem">
        <Box display="flex" alignItems="center">
          <h2>Paid Subscription</h2>
        </Box>
        <Box>
          {record.billing?.currentSubscription && (
            <SubscriptionView
              subscription={record.billing?.currentSubscription}
            />
          )}
        </Box>
      </Box>
      {!!record.billing?.subscriptions?.length && (
        <Box marginTop="2rem">
          <Box display="flex" alignItems="center">
            <Badge
              badgeContent={record.billing?.subscriptions.length}
              color="primary"
            >
              <h3>Subscription History</h3>
            </Badge>
            <IconButton
              sx={{ marginLeft: '1rem' }}
              onClick={onToggleHistoryExpansion}
            >
              {historyExpanded ? (
                <KeyboardArrowUp />
              ) : (
                <KeyboardArrowDown />
              )}
            </IconButton>
          </Box>
          {historyExpanded && (
            <Box>
              {record.billing?.subscriptions.map(
                (subscription: any) => (
                  <SubscriptionView
                    key={subscription.id}
                    subscription={subscription}
                  />
                ),
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export type SubscriptionViewProps = {
  subscription: any;
};

type RenewalStatus =
  | 'will_renew'
  | 'will_not_renew'
  | 'will_change_product'
  | 'will_pause'
  | 'requires_price_increase_consent'
  | 'has_already_renewed';

type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'expired'
  | 'in_grace_period'
  | 'in_billing_retry'
  | 'paused'
  | 'unknown'
  | 'incomplete';

export const SubscriptionView = ({
  subscription,
}: SubscriptionViewProps) => {
  const isExpired = subscription.current_period_ends_at < Date.now();
  const renewalStatus =
    subscription.auto_renewal_status as RenewalStatus;

  const renewalStatusColor = useMemo(() => {
    switch (renewalStatus) {
      case 'will_renew':
      case 'has_already_renewed':
        return 'success';
      case 'will_not_renew':
      case 'will_pause':
        return 'error';
      case 'will_change_product':
      case 'requires_price_increase_consent':
        return 'warning';
    }
  }, [renewalStatus]);

  const renewalStatusIcon = useMemo(() => {
    switch (renewalStatus) {
      case 'will_renew':
      case 'has_already_renewed':
        return <CheckIcon />;
      case 'will_not_renew':
      case 'will_pause':
        return <ClearIcon />;
      case 'will_change_product':
      case 'requires_price_increase_consent':
        return <PlusOneIcon />;
    }
  }, [renewalStatus]);

  const subscriptionStatus =
    subscription.status as SubscriptionStatus;

  const statusIcon = useMemo(() => {
    switch (subscriptionStatus) {
      case 'trialing':
      case 'active':
        return <CheckIcon />;
      case 'expired':
        return <DangerousIcon />;
      case 'incomplete':
      case 'paused':
      case 'unknown':
      case 'in_grace_period':
      case 'in_billing_retry':
        return <WarningIcon />;
    }
  }, [subscriptionStatus]);

  const statusColor = useMemo(() => {
    switch (subscriptionStatus) {
      case 'trialing':
        return 'secondary';
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'incomplete':
      case 'paused':
      case 'unknown':
      case 'in_grace_period':
      case 'in_billing_retry':
        return 'warning';
    }
  }, [subscriptionStatus]);

  return (
    <Box display="flex" alignItems="center">
      <Labeled label="First Created At">
        <GenericRelativeDateDisplay
          date={new Date(subscription.starts_at)}
        />
      </Labeled>
      <Labeled label="Current Period Start" marginLeft="1rem">
        <GenericRelativeDateDisplay
          date={new Date(subscription.current_period_starts_at)}
        />
      </Labeled>
      <Labeled label="Current Period End" marginLeft="1rem">
        <GenericRelativeDateDisplay
          color={isExpired ? 'error' : 'primary'}
          date={new Date(subscription.current_period_ends_at)}
        />
      </Labeled>
      <Labeled label="Availability" marginLeft="1rem">
        <Chip
          icon={
            subscription.gives_access ? (
              <CheckIcon />
            ) : (
              <DangerousIcon />
            )
          }
          color={subscription.gives_access ? 'success' : 'error'}
          label={
            subscription.gives_access ? 'Available' : 'Suspended'
          }
        />
      </Labeled>
      <Labeled label="Subscription Status" marginLeft="1rem">
        <Chip
          icon={statusIcon}
          color={statusColor}
          label={subscription.status}
        />
      </Labeled>
      <Labeled label="Renewal Status" marginLeft="1rem">
        <Chip
          icon={renewalStatusIcon}
          color={renewalStatusColor}
          label={renewalStatus}
        />
      </Labeled>
      {subscription.pending_payment && (
        <Labeled label="Warnings" marginLeft="1rem">
          <Chip
            icon={<WarningIcon />}
            color="warning"
            label="Pending Payment"
          />
        </Labeled>
      )}
    </Box>
  );
};
